import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const concepts = [
  {
    icon: '💰',
    title: '收入',
    subtitle: 'Income',
    description: '你的资金来源',
    example: '工资 · 副业 · 投资回报',
    color: '#FF8C00',
  },
  {
    icon: '💸',
    title: '支出',
    subtitle: 'Expense',
    description: '你的资金流出',
    example: '房租 · 餐饮 · 交通出行',
    color: '#E07A5F',
  },
  {
    icon: '📊',
    title: '储蓄',
    subtitle: 'Savings',
    description: '收入 − 支出 = 储蓄',
    example: '应急基金 · 定期存款',
    color: '#4ECDC4',
  },
  {
    icon: '📈',
    title: '投资',
    subtitle: 'Investment',
    description: '让钱生钱',
    example: '基金 · 股票 · 债券',
    color: '#95E1D3',
  },
];

const budgetRules = [
  { label: '必要支出', percent: 50, color: '#E07A5F', icon: '🏠', desc: '房租 · 水电 · 餐饮' },
  { label: '个人消费', percent: 30, color: '#FF8C00', icon: '🎮', desc: '娱乐 · 购物 · 社交' },
  { label: '储蓄投资', percent: 20, color: '#4ECDC4', icon: '🏦', desc: '存款 · 基金 · 保险' },
];

const keyStats = [
  { value: '50%', label: '必要支出占比', color: '#E07A5F' },
  { value: '6个月', label: '应急储备目标', color: '#4ECDC4' },
  { value: '72法则', label: '复利翻倍公式', color: '#FF8C00' },
  { value: '≥10%', label: '建议最低储蓄率', color: '#95E1D3' },
];

const insights = [
  {
    icon: '🧮',
    title: '72法则',
    desc: '投资翻倍年数 ≈ 72 ÷ 年收益率(%)',
    bg: 'rgba(255,140,0,0.08)',
    border: 'rgba(255,140,0,0.25)',
    titleColor: '#FFD700',
  },
  {
    icon: '🛡️',
    title: '应急基金',
    desc: '建议储备 3~6 个月的生活费用',
    bg: 'rgba(78,205,196,0.08)',
    border: 'rgba(78,205,196,0.25)',
    titleColor: '#4ECDC4',
  },
  {
    icon: '⏰',
    title: '复利效应',
    desc: '越早开始投资，时间越是你的朋友',
    bg: 'rgba(149,225,211,0.08)',
    border: 'rgba(149,225,211,0.25)',
    titleColor: '#95E1D3',
  },
];

const Coin: React.FC<{
  frame: number;
  delay: number;
  x: number;
  y?: number;
  size?: number;
}> = ({ frame, delay, x, y = 165, size = 60 }) => {
  const progress = spring({
    frame: frame - delay,
    fps: 30,
    config: { damping: 10, stiffness: 100 },
  });

  const translateY = interpolate(progress, [0, 1], [-150, 0]);
  const rotation = interpolate(progress, [0, 1], [0, 360 * 2]);
  const opacity = interpolate(progress, [0, 0.5, 1], [0, 1, 1]);

  if (frame < delay) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        fontSize: size,
        transform: `translateY(${translateY}px) rotate(${rotation}deg)`,
        opacity,
        filter: 'drop-shadow(0 10px 20px rgba(255, 140, 0, 0.3))',
      }}
    >
      🪙
    </div>
  );
};

const StatBadge: React.FC<{
  stat: (typeof keyStats)[0];
  index: number;
  frame: number;
  fps: number;
}> = ({ stat, index, frame, fps }) => {
  const delay = 15 + index * 10;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '10px 24px',
        background: `${stat.color}15`,
        border: `1px solid ${stat.color}40`,
        borderRadius: 12,
        opacity: progress,
        transform: `scale(${interpolate(progress, [0, 1], [0.8, 1])})`,
      }}
    >
      <span style={{ fontSize: 32, fontWeight: 800, color: stat.color }}>
        {stat.value}
      </span>
      <span
        style={{
          fontSize: 18,
          color: 'rgba(255,255,255,0.55)',
          marginTop: 2,
          whiteSpace: 'nowrap' as const,
        }}
      >
        {stat.label}
      </span>
    </div>
  );
};

const ConceptCard: React.FC<{
  concept: (typeof concepts)[0];
  index: number;
  frame: number;
  fps: number;
}> = ({ concept, index, frame, fps }) => {
  const delay = 30 + index * 20;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const scale = interpolate(progress, [0, 1], [0.8, 1]);

  if (frame < delay) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: 60 + index * 455,
        top: 250,
        width: 395,
        padding: '22px',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(10px)',
        borderRadius: 16,
        border: `2px solid ${concept.color}40`,
        opacity: progress,
        transform: `scale(${scale})`,
        boxShadow: `0 8px 32px ${concept.color}20`,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        textAlign: 'center' as const,
      }}
    >
      <div style={{ fontSize: 46, marginBottom: 6 }}>{concept.icon}</div>
      <h3 style={{ fontSize: 34, fontWeight: 700, margin: 0, color: '#fff' }}>
        {concept.title}
      </h3>
      <p style={{ fontSize: 18, margin: '2px 0 0 0', color: concept.color }}>
        {concept.subtitle}
      </p>
      <p
        style={{
          fontSize: 22,
          margin: '8px 0 0 0',
          color: 'rgba(255, 255, 255, 0.7)',
        }}
      >
        {concept.description}
      </p>
      <p
        style={{
          fontSize: 17,
          margin: '6px 0 0 0',
          color: 'rgba(255, 255, 255, 0.4)',
          fontStyle: 'italic' as const,
        }}
      >
        {concept.example}
      </p>
    </div>
  );
};

const BudgetBar: React.FC<{ frame: number; fps: number }> = ({
  frame,
  fps,
}) => {
  const delay = 95;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 14, stiffness: 180 },
  });

  if (frame < delay) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: 80,
        right: 80,
        top: 570,
        opacity: progress,
        transform: `translateY(${interpolate(progress, [0, 1], [30, 0])}px)`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 15,
        }}
      >
        <span style={{ fontSize: 30, fontWeight: 700, color: '#fff' }}>
          💡 50 / 30 / 20 预算法则
        </span>
        <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.4)' }}>
          经典预算分配方案
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          height: 64,
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {budgetRules.map((rule, i) => {
          const segProgress = spring({
            frame: frame - delay - i * 8,
            fps,
            config: { damping: 12, stiffness: 200 },
          });
          return (
            <div
              key={i}
              style={{
                width: `${rule.percent * segProgress}%`,
                background: `${rule.color}CC`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                overflow: 'hidden',
                whiteSpace: 'nowrap' as const,
              }}
            >
              <span style={{ fontSize: 30 }}>{rule.icon}</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>
                {rule.label} {rule.percent}%
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', marginTop: 8 }}>
        {budgetRules.map((rule, i) => (
          <div
            key={i}
            style={{
              width: `${rule.percent}%`,
              textAlign: 'center' as const,
              fontSize: 24,
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            {rule.desc}
          </div>
        ))}
      </div>
    </div>
  );
};

const FinancialInsight: React.FC<{ frame: number; fps: number }> = ({
  frame,
  fps,
}) => {
  const delay = 110;
  const progress = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  if (frame < delay) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: 80,
        right: 80,
        top: 780,
        display: 'flex',
        gap: 24,
        opacity: progress,
        transform: `translateY(${interpolate(progress, [0, 1], [20, 0])}px)`,
      }}
    >
      {insights.map((item, i) => {
        const itemProgress = spring({
          frame: frame - delay - i * 8,
          fps,
          config: { damping: 12, stiffness: 200 },
        });
        return (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '18px 24px',
              background: item.bg,
              border: `1px solid ${item.border}`,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              opacity: itemProgress,
              transform: `translateY(${interpolate(itemProgress, [0, 1], [15, 0])}px)`,
            }}
          >
            <span style={{ fontSize: 44 }}>{item.icon}</span>
            <div>
              <p
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: item.titleColor,
                  margin: 0,
                }}
              >
                {item.title}
              </p>
              <p
                style={{
                  fontSize: 22,
                  color: 'rgba(255,255,255,0.5)',
                  margin: '4px 0 0 0',
                }}
              >
                {item.desc}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Formula: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const progress = spring({
    frame: frame - 130,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  if (frame < 130) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 45,
        left: '50%',
        transform: `translateX(-50%) scale(${progress})`,
        opacity: progress,
        padding: '18px 40px',
        background:
          'linear-gradient(135deg, rgba(255, 140, 0, 0.15), rgba(78, 205, 196, 0.15))',
        border: '2px solid rgba(255, 140, 0, 0.4)',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 28 }}>💰</span>
        <span style={{ fontSize: 38, fontWeight: 700, color: '#fff' }}>
          收入
        </span>
      </div>
      <span style={{ fontSize: 38, color: 'rgba(255,255,255,0.5)' }}>−</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 28 }}>💸</span>
        <span style={{ fontSize: 38, fontWeight: 700, color: '#fff' }}>
          支出
        </span>
      </div>
      <span style={{ fontSize: 38, color: 'rgba(255,255,255,0.5)' }}>=</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 28 }}>📊</span>
        <span style={{ fontSize: 38, fontWeight: 700, color: '#4ECDC4' }}>
          储蓄
        </span>
      </div>
      <span style={{ fontSize: 38, color: 'rgba(255,255,255,0.5)' }}>→</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 28 }}>📈</span>
        <span style={{ fontSize: 38, fontWeight: 700, color: '#95E1D3' }}>
          投资
        </span>
      </div>
    </div>
  );
};

export const FinancialLiteracyAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgProgress = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const titleProgress = spring({
    frame: frame - 10,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  const titleY = interpolate(titleProgress, [0, 1], [-80, 0]);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
        fontFamily: 'system-ui, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* 装饰背景 */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          background:
            'radial-gradient(circle, rgba(255, 140, 0, 0.1), transparent)',
          borderRadius: '50%',
          opacity: bgProgress,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -200,
          left: -200,
          width: 600,
          height: 600,
          background:
            'radial-gradient(circle, rgba(78, 205, 196, 0.1), transparent)',
          borderRadius: '50%',
          opacity: bgProgress,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 300,
          left: '50%',
          width: 900,
          height: 900,
          background:
            'radial-gradient(circle, rgba(149, 225, 211, 0.04), transparent)',
          borderRadius: '50%',
          opacity: bgProgress,
          transform: 'translateX(-50%)',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 35,
          left: 80,
          opacity: titleProgress,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 80,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FF8C00, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          财商入门
        </h1>
        <p
          style={{
            fontSize: 26,
            margin: '8px 0 0 0',
            color: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          4 个核心概念 · Financial Literacy Basics
        </p>
      </div>

      {/* 关键数据统计 */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          right: 80,
          display: 'flex',
          gap: 16,
        }}
      >
        {keyStats.map((stat, i) => (
          <StatBadge key={i} stat={stat} index={i} frame={frame} fps={fps} />
        ))}
      </div>

      {/* 硬币动画 */}
      {[0, 1, 2].map((i) => (
        <Coin
          key={`r-${i}`}
          frame={frame}
          delay={5 + i * 8}
          x={1540 + i * 75}
          y={160}
          size={50}
        />
      ))}
      {/* 概念卡片 */}
      {concepts.map((concept, index) => (
        <ConceptCard
          key={index}
          concept={concept}
          index={index}
          frame={frame}
          fps={fps}
        />
      ))}

      {/* 50/30/20 预算法则 */}
      <BudgetBar frame={frame} fps={fps} />

      {/* 财务洞见 */}
      <FinancialInsight frame={frame} fps={fps} />

      {/* 核心公式 */}
      <Formula frame={frame} fps={fps} />
    </AbsoluteFill>
  );
};
